import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Truck, Search, Camera, Tag, CreditCard, Package, MessageSquare, Star, HandshakeIcon, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const stepIcons = {
  buy: [Search, MessageSquare, CreditCard, Package],
  sell: [Camera, Tag, HandshakeIcon, Truck],
};

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-[#D6E8F0] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-[#F0F7FA] transition-colors" data-testid={`faq-item-${idx}`}>
        <span className="font-semibold text-[#033871] text-sm sm:text-base pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#0C63BF] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-sm text-[#5A8BA8] leading-relaxed">{a}</div>}
    </div>
  );
};

export const HowItWorksPage = () => {
  const { t, i18n } = useTranslation();
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "inLanguage": i18n.language,
    "mainEntity": [1, 2, 3, 4, 5, 6].map(i => ({
      "@type": "Question",
      "name": t(`hiw.faq${i}_q`),
      "acceptedAnswer": { "@type": "Answer", "text": t(`hiw.faq${i}_a`) }
    }))
  };
  return (
    <div className="min-h-screen bg-[#F0F7FA]" data-testid="how-it-works-page">
      <SEO
        title={t('hiw.seo_title')}
        description={t('hiw.seo_desc')}
        path="/comment-ca-marche"
        jsonLd={faqJsonLd}
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#021E47] to-[#033871] text-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">
            {t('hiw.hero_title')}
          </h1>
          <p className="text-base sm:text-lg text-[#B8D4E3] max-w-xl mx-auto">
            {t('hiw.hero_sub')}
          </p>
        </div>
      </div>

      {/* Buy Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-[#0C63BF] text-white font-bold rounded-full text-xs tracking-wider mb-3">
              {t('hiw.buy_badge')}
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#033871]">
              {t('hiw.buy_title')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stepIcons.buy.map((Icon, i) => (
              <div key={i} className="relative">
                <div className="bg-[#F0F7FA] rounded-2xl p-6 h-full">
                  <div className="w-10 h-10 bg-[#0C63BF] rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-[10px] font-bold text-[#0C63BF] tracking-wider">{t('hiw.step_label', { n: i + 1 })}</span>
                  <h3 className="font-bold text-[#033871] mt-1 mb-2">{t(`hiw.buy${i + 1}_title`)}</h3>
                  <p className="text-[#5A8BA8] text-sm leading-relaxed">{t(`hiw.buy${i + 1}_desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/catalogue">
              <Button className="bg-[#0C63BF] hover:bg-[#033871] text-white h-11 rounded-xl font-bold gap-2">
                {t('hiw.see_listings')} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sell Section */}
      <section className="py-12 sm:py-16 bg-[#F0F7FA]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-[#32CD32] text-white font-bold rounded-full text-xs tracking-wider mb-3">
              {t('hiw.sell_badge')}
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#033871]">
              {t('hiw.sell_title')}
            </h2>
            <p className="text-[#5A8BA8] mt-2">{t('hiw.sell_sub')}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stepIcons.sell.map((Icon, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#D6E8F0] h-full">
                <div className="w-10 h-10 bg-[#32CD32]/10 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#32CD32]" />
                </div>
                <span className="text-[10px] font-bold text-[#32CD32] tracking-wider">{t('hiw.step_label', { n: i + 1 })}</span>
                <h3 className="font-bold text-[#033871] mt-1 mb-2">{t(`hiw.sell${i + 1}_title`)}</h3>
                <p className="text-[#5A8BA8] text-sm leading-relaxed">{t(`hiw.sell${i + 1}_desc`)}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/vendre">
              <Button className="bg-[#32CD32] hover:bg-[#28a428] text-white h-11 rounded-xl font-bold gap-2">
                {t('hiw.sell_cta')} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Protection & Trust */}
      <section className="py-12 sm:py-16 bg-[#021E47]">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10">
            {t('hiw.why_title')}
          </h2>

          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-[#0C63BF]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-[#0C63BF]" />
              </div>
              <h3 className="font-bold text-white text-lg mb-2">{t('hiw.prot_title')}</h3>
              <p className="text-[#B8D4E3] text-sm leading-relaxed">{t('hiw.prot_desc')}</p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-[#32CD32]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Tag className="w-7 h-7 text-[#32CD32]" />
              </div>
              <h3 className="font-bold text-white text-lg mb-2">{t('hiw.comm_title')}</h3>
              <p className="text-[#B8D4E3] text-sm leading-relaxed">{t('hiw.comm_desc')}</p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-[#0C63BF]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star className="w-7 h-7 text-[#0C63BF]" />
              </div>
              <h3 className="font-bold text-white text-lg mb-2">{t('hiw.spec_title')}</h3>
              <p className="text-[#B8D4E3] text-sm leading-relaxed">{t('hiw.spec_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Fees explainer */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#033871] text-center mb-10">
            {t('hiw.cost_title')}
          </h2>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-[#F0F7FA] rounded-2xl p-6 border-2 border-[#32CD32]" data-testid="fees-seller">
              <h3 className="font-bold text-[#033871] text-lg mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-[#32CD32] rounded-lg flex items-center justify-center text-white text-xs font-black">V</span>
                {t('hiw.seller')}
              </h3>
              <div className="text-center py-4">
                <p className="text-5xl font-black text-[#32CD32]">0 EUR</p>
                <p className="text-[#5A8BA8] text-sm mt-2">{t('hiw.seller_free')}</p>
              </div>
              <ul className="text-sm text-[#5A8BA8] space-y-2 mt-4">
                <li className="flex items-center gap-2"><span className="text-[#32CD32] font-bold">&#10003;</span> {t('hiw.seller_li1')}</li>
                <li className="flex items-center gap-2"><span className="text-[#32CD32] font-bold">&#10003;</span> {t('hiw.seller_li2')}</li>
                <li className="flex items-center gap-2"><span className="text-[#32CD32] font-bold">&#10003;</span> {t('hiw.seller_li3')}</li>
              </ul>
            </div>

            <div className="bg-[#F0F7FA] rounded-2xl p-6 border border-[#D6E8F0]" data-testid="fees-buyer">
              <h3 className="font-bold text-[#033871] text-lg mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-[#0C63BF] rounded-lg flex items-center justify-center text-white text-xs font-black">A</span>
                {t('hiw.buyer')}
              </h3>
              <div className="text-center py-4">
                <p className="text-lg font-bold text-[#033871]">{t('hiw.buyer_l1')}</p>
                <p className="text-lg font-bold text-[#033871] mt-1">{t('hiw.buyer_l2')}</p>
                <p className="text-lg font-bold text-[#0C63BF] mt-1">{t('hiw.buyer_l3')}</p>
                <p className="text-[#5A8BA8] text-sm mt-3">{t('hiw.buyer_note')}</p>
              </div>
              <ul className="text-sm text-[#5A8BA8] space-y-2 mt-4">
                <li className="flex items-center gap-2"><span className="text-[#0C63BF] font-bold">&#10003;</span> {t('hiw.buyer_li1')}</li>
                <li className="flex items-center gap-2"><span className="text-[#0C63BF] font-bold">&#10003;</span> {t('hiw.buyer_li2')}</li>
                <li className="flex items-center gap-2"><span className="text-[#0C63BF] font-bold">&#10003;</span> {t('hiw.buyer_li3')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 bg-[#F0F7FA]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#033871] text-center mb-10">
            {t('hiw.faq_title')}
          </h2>
          <div className="space-y-5" data-testid="faq-section">
            {[1, 2, 3, 4, 5, 6].map(i => <FAQItem key={i} idx={i} q={t(`hiw.faq${i}_q`)} a={t(`hiw.faq${i}_a`)} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#033871] mb-3">
            {t('hiw.cta_title')}
          </h2>
          <p className="text-[#5A8BA8] mb-8">
            {t('hiw.cta_sub')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/catalogue">
              <Button className="bg-[#0C63BF] hover:bg-[#033871] text-white h-11 rounded-xl font-bold gap-2 w-full sm:w-auto">
                {t('hiw.cta_buy')} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/vendre">
              <Button className="bg-[#32CD32] hover:bg-[#28a428] text-white h-11 rounded-xl font-bold gap-2 w-full sm:w-auto">
                {t('hiw.cta_sell')} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
